/* eslint-disable  */
import { useNewToast } from 'hooks/newToast';
import { useSafra } from 'hooks/providers/configuracoes/safras';
import React, { useState, useCallback, useContext, createContext } from 'react';
import api from 'services';
import { formatSecador } from 'utils/formaters/rastreabilidade/secador';
import { toDate } from 'utils/converters';
import { appWarnings } from 'utils/appWarnings';

const success = {
  type: 'success',
};

function mapEntradasToLote(entradas) {
  if (!Array.isArray(entradas)) return [];
  return entradas.map((entrada) => {
    const payload = {
      id: entrada.ref_id,
      codigo: entrada.codigo ?? '',
      quantidade: Number(entrada.quantidade) || 0,
    };
    if (entrada.ref_tipo != null && entrada.ref_tipo !== '') {
      payload.ref_tipo = entrada.ref_tipo;
    }
    return payload;
  });
}

function buildSecagemPayload(secagem, overrides = {}) {
  const entradas = secagem?.entradas ?? [];
  const lote = mapEntradasToLote(entradas);
  return {
    secador: overrides.secador ?? secagem?.glo_secador_id,
    lote,
    dataEntrada:
      overrides.dataEntrada != null
        ? toDate(overrides.dataEntrada)
        : secagem?.dataEntrada
          ? toDate(secagem.dataEntrada)
          : '',
    dataSaida:
      overrides.dataSaida != null
        ? overrides.dataSaida
          ? toDate(overrides.dataSaida)
          : null
        : secagem?.dataSaida
          ? toDate(secagem.dataSaida)
          : null,
    horaEntrada: overrides.horaEntrada ?? secagem?.horaEntrada ?? '',
    horaSaida: overrides.horaSaida ?? secagem?.horaSaida ?? null,
    umidadeEntrada: Number(secagem?.umidadeEntrada ?? 0),
    umidadeSaida: Number(secagem?.umidadeSaida ?? 0),
    responsavel: secagem?.responsavel ?? '',
  };
}

const SecadorContext = createContext({});

export const SecadorProvider = ({ children }) => {
  const { showToast } = useNewToast();
  const { safraAtual } = useSafra();

  const [lotesSecador, setLotesSecador] = useState([]);
  const [lotesSecadorLoading, setLotesSecadorLoading] = useState(false);

  const [secadorLotesHistorico, setSecadorLotesHistorico] = useState([]);
  const [secadorLotesHistoricoLoading, setSecadorLotesHistoricoLoading] =
    useState(false);

  const [secagem, setSecagem] = useState({});
  const [secagemLoading, setSecagemLoading] = useState(false);

  const [gerarNoSecadorLoading, setGerarNoSecadorLoading] = useState(false);
  const [deleteNoSecadorLoading, setDeleteNoSecadorLoading] = useState(false);
  const [editarNoSecadorLoading, setEditarNoSecadorLoading] = useState(false);
  const [trocarSecadorLoading, setTrocarSecadorLoading] = useState(false);

  const [ocorrenciasSecagem, setOcorrenciasSecagem] = useState([]);
  const [ocorrenciasSecagemLoading, setOcorrenciasSecagemLoading] =
    useState(false);
  const [createOcorrenciaLoading, setCreateOcorrenciaLoading] = useState(false);
  const [updateOcorrenciaLoading, setUpdateOcorrenciaLoading] = useState(false);
  const [deleteOcorrenciaLoading, setDeleteOcorrenciaLoading] = useState(false);

  const getLotesSecador = useCallback(async (filter) => {
    try {
      setLotesSecadorLoading(true);
      const response = await api.secador().getLotesSecador(filter);
      setLotesSecador(response);
      setLotesSecadorLoading(false);
    } catch (errors) {
      setLotesSecadorLoading(false);
    }
  }, []);

  const getSecagem = useCallback(async (id, tipo) => {
    try {
      setSecagemLoading(true);

      let response = {};

      if (tipo === 'secagem') {
        response = await api.secador().getSecagem(id);
      } else {
        response = await api.secador().getRessecagem(id);
      }

      setSecagem(response);
      setSecagemLoading(false);
      return response;
    } finally {
      setSecagemLoading(false);
    }
  }, []);

  const getLotesSecadorHistorico = useCallback(async (filters) => {
    try {
      setSecadorLotesHistoricoLoading(true);
      const response = await api.secador().getLotesSecadorHistorico(filters);
      setSecadorLotesHistorico(response.content);
      setSecadorLotesHistoricoLoading(false);
    } catch (errors) {
      setSecadorLotesHistoricoLoading(false);
    }
  }, []);

  const gerarNoSecador = useCallback(
    async (dados) => {
      const newData = {
        ...formatSecador(dados),
        safra: dados?.safra ?? safraAtual?.id,
      };

      try {
        setGerarNoSecadorLoading(true);
        const response = await api.secador().gerarNoSecador(newData);
        setGerarNoSecadorLoading(false);
        showToast(success);
        return response;
      } catch (err) {
        showToast(appWarnings.Danger(err));
        setGerarNoSecadorLoading(false);
        throw new Error(err);
      }
    },
    [showToast, safraAtual],
  );

  const editarNoSecador = useCallback(
    async (id, dados, tipo = 'secagem') => {
      const newData = formatSecador(dados);

      try {
        setEditarNoSecadorLoading(true);

        const apiFunctions = {
          secagem: {
            apiFunction: (id, data) => api.secador().editarNoSecador(id, data),
            successMessage: 'Entrada no secador editada com sucesso!',
          },
          ressecagem: {
            apiFunction: (id, data) => api.secador().updateRessecagem(id, data),
            successMessage: 'Entrada na ressecagem editada com sucesso!',
          },
        };

        const { apiFunction, successMessage } = apiFunctions[tipo];

        const response = await apiFunction(id, newData);

        setEditarNoSecadorLoading(false);
        showToast({ ...success, message: successMessage });
        return response;
      } catch (err) {
        showToast(appWarnings.Danger(err));
        setEditarNoSecadorLoading(false);
        throw new Error(err);
      }
    },
    [showToast],
  );

  const deleteNoSecador = useCallback(
    async (id, tipo) => {
      try {
        setDeleteNoSecadorLoading(true);

        if (tipo === 'secagem') {
          await api.secador().deleteNoSecador(id);
        } else {
          await api.secador().deleteRessecagem(id);
        }

        setDeleteNoSecadorLoading(false);
        getLotesSecador();
        showToast({
          ...success,
          message: 'Processo de secagem removido com sucesso!',
        });
      } catch (err) {
        showToast(appWarnings.Danger(err));
        setDeleteNoSecadorLoading(false);
      }
    },
    [showToast, getLotesSecador],
  );

  const getOcorrenciasSecagem = useCallback(async (id) => {
    try {
      setOcorrenciasSecagemLoading(true);
      const response = await api.secador().listOcorrenciasSecagem(id);
      setOcorrenciasSecagem(response);
      setOcorrenciasSecagemLoading(false);
      return response;
    } catch (err) {
      setOcorrenciasSecagemLoading(false);
      return [];
    }
  }, []);

  const createOcorrenciaSecagem = useCallback(
    async (id, ocorrencia) => {
      try {
        setCreateOcorrenciaLoading(true);
        const response = await api
          .secador()
          .createOcorrenciaSecagem(id, ocorrencia);
        showToast({
          ...success,
          message: 'Ocorrência criada com sucesso!',
        });
        return response;
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setCreateOcorrenciaLoading(false);
        await getOcorrenciasSecagem(id);
      }
    },
    [showToast, getOcorrenciasSecagem],
  );

  const updateOcorrenciaSecagem = useCallback(
    async (id, ocorrenciaId, ocorrencia) => {
      try {
        setUpdateOcorrenciaLoading(true);
        const response = await api
          .secador()
          .updateOcorrenciaSecagem(id, ocorrenciaId, ocorrencia);
        showToast({
          ...success,
          message: 'Ocorrência atualizada com sucesso!',
        });
        return response;
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setUpdateOcorrenciaLoading(false);
        await getOcorrenciasSecagem(id);
      }
    },
    [showToast, getOcorrenciasSecagem],
  );

  const deleteOcorrenciaSecagem = useCallback(
    async (id, ocorrenciaId) => {
      try {
        setDeleteOcorrenciaLoading(true);
        const response = await api
          .secador()
          .deleteOcorrenciaSecagem(id, ocorrenciaId);
        showToast({
          ...success,
          message: 'Ocorrência excluída com sucesso!',
        });
        return response;
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setDeleteOcorrenciaLoading(false);
        await getOcorrenciasSecagem(id);
      }
    },
    [showToast, getOcorrenciasSecagem],
  );

  const finalizarSecagem = useCallback(
    async (secagemId, secagemData, { dataSaida, horaSaida } = {}) => {
      try {
        setEditarNoSecadorLoading(true);

        const agora = new Date();
        const resolvedDataSaida = dataSaida || agora.toLocaleDateString('en-CA', {
          timeZone: 'America/Sao_Paulo',
        });
        const resolvedHoraSaida = horaSaida || agora.toLocaleTimeString('en-GB', {
          timeZone: 'America/Sao_Paulo',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });

        const payload = buildSecagemPayload(secagemData, { dataSaida: resolvedDataSaida, horaSaida: resolvedHoraSaida });
        await api.secador().editarNoSecador(secagemId, payload);

        showToast({ ...success, message: 'Secagem finalizada com sucesso!' });
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw err;
      } finally {
        setEditarNoSecadorLoading(false);
      }
    },
    [showToast],
  );

  const editarFimSecagem = useCallback(
    async (secagemId, secagemData, { dataSaida, horaSaida }) => {
      try {
        setEditarNoSecadorLoading(true);

        const payload = buildSecagemPayload(secagemData, { dataSaida, horaSaida });
        await api.secador().editarNoSecador(secagemId, payload);

        showToast({
          ...success,
          message: 'Fim da secagem atualizado com sucesso!',
        });
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw err;
      } finally {
        setEditarNoSecadorLoading(false);
        await getOcorrenciasSecagem(secagemId);
      }
    },
    [showToast, getOcorrenciasSecagem],
  );

  const cancelarFimSecagem = useCallback(
    async (secagemId, secagemData) => {
      try {
        setEditarNoSecadorLoading(true);

        const payload = buildSecagemPayload(secagemData, {
          dataSaida: '',
          horaSaida: '',
        });
        await api.secador().editarNoSecador(secagemId, payload);

        showToast({
          ...success,
          message: 'Fim da secagem cancelado com sucesso!',
        });
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw err;
      } finally {
        setEditarNoSecadorLoading(false);
        await getOcorrenciasSecagem(secagemId);
      }
    },
    [showToast, getOcorrenciasSecagem],
  );

  const trocarSecador = useCallback(
    async (secagemId, { dataTroca, horaTroca, secadorId }) => {
      try {
        setTrocarSecadorLoading(true);
        const secagem = await api.secador().getSecagem(secagemId);
        if (!secagem?.id) {
          throw new Error('Secagem não encontrada');
        }

        const putPayload = buildSecagemPayload(secagem, {
          dataSaida: dataTroca,
          horaSaida: horaTroca,
        });
        await api.secador().editarNoSecador(secagemId, putPayload);

        const postPayload = {
          ...buildSecagemPayload(secagem, {
            secador: secadorId,
            dataEntrada: dataTroca,
            horaEntrada: horaTroca,
            dataSaida: null,
            horaSaida: null,
          }),
          safra: secagem?.glo_safra_id ?? safraAtual?.id,
        };
        await api.secador().gerarNoSecador(postPayload);

        await getLotesSecador();
        showToast({
          ...success,
          message: 'Secagem transferida para o novo secador com sucesso!',
        });
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw err;
      } finally {
        setTrocarSecadorLoading(false);
      }
    },
    [showToast, getLotesSecador, safraAtual],
  );

  return (
    <SecadorContext.Provider
      value={{
        getLotesSecador,
        lotesSecador,
        deleteNoSecador,
        deleteNoSecadorLoading,
        gerarNoSecador,
        gerarNoSecadorLoading,
        getLotesSecadorHistorico,
        lotesSecadorLoading,
        secadorLotesHistorico,
        secadorLotesHistoricoLoading,
        editarNoSecadorLoading,
        editarNoSecador,
        getSecagem,
        secagem,
        secagemLoading,
        getOcorrenciasSecagem,
        ocorrenciasSecagem,
        ocorrenciasSecagemLoading,
        createOcorrenciaSecagem,
        createOcorrenciaLoading,
        updateOcorrenciaSecagem,
        updateOcorrenciaLoading,
        deleteOcorrenciaSecagem,
        deleteOcorrenciaLoading,
        trocarSecador,
        trocarSecadorLoading,
        finalizarSecagem,
        editarFimSecagem,
        cancelarFimSecagem,
      }}
    >
      {children}
    </SecadorContext.Provider>
  );
};

export function useSecador() {
  const context = useContext(SecadorContext);

  if (!context)
    throw new Error('useSecador must be used within an AuthProvider');

  return context;
}
